// gallery.js — Wonder — prêt à coller
// ------------------------------------------------------------
import Swiper from "./swiper-bundle.esm.browser.min.js";
import PhotoSwipeLightbox from "./photoswipe-lightbox.esm.min.js";

/* ---------- Lightbox: desktop uniquement ---------- */
const mqlDesktop = window.matchMedia("(min-width: 769px)");

// Sur desktop: activer PhotoSwipe + masquer les badges pendant l'ouverture
if (mqlDesktop.matches) {
  const badges = document.querySelector(".photoswipe > .card__badges");
  const lightbox = new PhotoSwipeLightbox({
    gallery: ".photoswipe",
    children: "a",
    pswpModule: () => import("./photoswipe.esm.min.js"),
    bgOpacity: 1,
  });
  lightbox.init();
  lightbox.on("beforeOpen", () => badges?.classList.add("hide"));
  lightbox.on("destroy", () => badges?.classList.remove("hide"));
} else {
  // Sur mobile: empêcher l'ouverture d'un nouvel onglet au tap sur l'image
  document.querySelectorAll(".photoswipe a").forEach((a) =>
    a.addEventListener(
      "click",
      (e) => {
        e.preventDefault();
      },
      { passive: false }
    )
  );
}
// Si on passe le breakpoint (rotation, redimensionnement), on recharge proprement
mqlDesktop.addEventListener?.("change", () => location.reload());

/* ---------- Web Component ---------- */
if (!customElements.get("gallery-section")) {
  customElements.define(
    "gallery-section",
    class GallerySection extends HTMLElement {
      /* ========= Utils ========= */
      safeParse = (el, fallback = {}) => {
        try {
          const t = el?.innerHTML?.trim();
          return t ? JSON.parse(t) : fallback;
        } catch {
          return fallback;
        }
      };
      naturalCompare = (a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });

      // Debounce simple
      debounce(fn, ms = 60) {
        let t;
        return (...a) => {
          clearTimeout(t);
          t = setTimeout(() => fn.apply(this, a), ms);
        };
      }

      // ID stable commun thumbs <-> gallery
      computeStableId = (slide) => {
        const media = slide.querySelector("img, video");
        const shopifyId =
          media?.getAttribute("data-media-id") ||
          slide.getAttribute("data-slide-media-id");
        if (shopifyId) return String(shopifyId).trim();

        const pickSrc = () => {
          let src =
            media?.currentSrc ||
            media?.getAttribute?.("src") ||
            media?.getAttribute?.("data-src") ||
            "";
          if (!src && media?.tagName === "VIDEO") {
            const s = media.querySelector("source[src]");
            src = s?.getAttribute("src") || "";
          }
          return (src || "").split("?")[0];
        };
        let file = (pickSrc().split("/").pop() || "").toLowerCase();
        file = file.replace(
          /_(?:\d+x\d+|\d+x|crop_[^_.]+|center|left|right|top|bottom|progressive|retina|pico|icon|thumb|small|compact|medium|large|grande|\d{3,4}x\d{3,4})+(?=\.)/gi,
          ""
        );
        if (file) return file;

        const alt = (media?.getAttribute("alt") || "").trim().toLowerCase();
        return alt || `no-id-${Math.random().toString(36).slice(2, 9)}`;
      };

      ensureStableIdsBoth = (galleryContainer, thumbsContainer) => {
        [galleryContainer, thumbsContainer].forEach((container) => {
          if (!container) return;
          container.querySelectorAll("[data-swiper-slide]").forEach((slide) => {
            const id = this.computeStableId(slide);
            slide.setAttribute("data-slide-media-id", id);
            const media = slide.querySelector("img, video");
            if (media) media.setAttribute("data-media-id", id);
          });
        });
      };

      buildMasterOrder = (containerEl) => {
        const slides = Array.from(
          containerEl.querySelectorAll("[data-swiper-slide]")
        );
        // NE PAS trier : respecter l’ordre réel
        return slides.map((s) => s.getAttribute("data-slide-media-id") || "");
      };

      sortByMaster = (slides) => {
        const idx = (id) => this.masterOrder.indexOf(String(id));
        slides.sort((s1, s2) => {
          const a = s1.getAttribute("data-slide-media-id") || "";
          const b = s2.getAttribute("data-slide-media-id") || "";
          const ia = idx(a),
            ib = idx(b);
          if (ia === -1 && ib === -1) return this.naturalCompare(a, b);
          if (ia === -1) return 1;
          if (ib === -1) return -1;
          return ia - ib;
        });
      };

      sortContainerByMaster = (containerEl) => {
        if (!containerEl) return;
        const slides = Array.from(
          containerEl.querySelectorAll("[data-swiper-slide]")
        );
        this.sortByMaster(slides);
        slides.forEach((s) => containerEl.appendChild(s));
      };

      syncThumbsToGalleryOrder = () => {
        if (!this.thumbsSwiper) return;
        const gReal = Array.from(
          this.elements.gallery.querySelectorAll("[data-swiper-slide]")
        ).filter((s) => !s.classList.contains("swiper-slide-duplicate"));
        const gIds = gReal.map(
          (s) => s.getAttribute("data-slide-media-id") || ""
        );

        const tReal = Array.from(
          this.elements.thumbs.querySelectorAll("[data-swiper-slide]")
        ).filter((s) => !s.classList.contains("swiper-slide-duplicate"));

        tReal.sort((a, b) => {
          const ia = gIds.indexOf(a.getAttribute("data-slide-media-id") || "");
          const ib = gIds.indexOf(b.getAttribute("data-slide-media-id") || "");
          return ia - ib;
        });

        this.thumbsSwiper.removeAllSlides();
        this.thumbsSwiper.appendSlide(tReal);
        this.thumbsSwiper.updateSlides();
        this.thumbsSwiper.update();
      };

      // Attendre le chargement des images de la gallery
      waitForImages(root) {
        const imgs = [
          ...(root || this.elements?.gallery || document).querySelectorAll(
            "img"
          ),
        ];
        const pending = imgs.filter((img) => !img.complete);
        if (pending.length === 0) return Promise.resolve();
        return new Promise((resolve) => {
          let left = pending.length,
            done = () => {
              if (--left <= 0) resolve();
            };
          pending.forEach((img) => {
            img.addEventListener("load", done, { once: true });
            img.addEventListener("error", done, { once: true });
          });
          setTimeout(resolve, 800);
        });
      }

      /* ======== Fallback / reset ======== */
      resetGalleryPosition() {
        const gs = this.gallerySwiper;
        if (!gs || !gs.wrapperEl) return;
        const w = gs.wrapperEl;
        w.style.transitionDuration = "0ms";
        w.style.transform = "translate3d(0,0,0)";
        gs.setTranslate?.(0);
        gs.activeIndex = 0;
        gs.updateSlides();
        gs.updateSlidesClasses?.();
      }

      hardSlideTo(idx) {
        const gs = this.gallerySwiper;
        if (!gs) return;

        const slides = [...gs.slides].filter(
          (s) => !s.classList.contains("swiper-slide-duplicate")
        );
        const target = slides[idx];
        if (!target) return;

        if (!target.offsetWidth || !gs.width) {
          setTimeout(() => this.hardSlideTo(idx), 50);
          return;
        }

        const horizontal = gs.isHorizontal ? gs.isHorizontal() : true;
        const offset = horizontal ? target.offsetLeft : target.offsetTop;
        const w = gs.wrapperEl;

        w.style.transitionDuration = "0ms";
        w.style.transform = horizontal
          ? `translate3d(${-offset}px, 0, 0)`
          : `translate3d(0, ${-offset}px, 0)`;

        slides.forEach((s, i) => {
          s.classList.toggle("swiper-slide-active", i === idx);
          s.classList.toggle("swiper-slide-prev", i === idx - 1);
          s.classList.toggle("swiper-slide-next", i === idx + 1);
        });
        gs.activeIndex = idx;
        gs.updateSlidesClasses?.();
        gs.emit?.("slideChange");
      }

      repositionActive = () => {
        const gs = this.gallerySwiper;
        if (!gs) return;
        const idx = Number.isFinite(gs.activeIndex) ? gs.activeIndex : 0;

        gs.updateSize();
        gs.updateSlides();
        gs.updateProgress();
        if (this._useHardSlide) this.hardSlideTo(idx);
        else gs.slideTo(idx, 0, false);
      };

      probeSwiperBroken() {
        const gs = this.gallerySwiper;
        if (!gs) {
          this._useHardSlide = true;
          return;
        }
        const w = gs.wrapperEl;
        const beforeT = getComputedStyle(w).transform;
        const beforeI = gs.activeIndex;

        const tryIdx = Math.min(1, [...gs.slides].length - 1);
        gs.updateSlides();
        if (gs.params.loop) gs.slideToLoop(tryIdx, 0, false);
        else gs.slideTo(tryIdx, 0, false);

        setTimeout(() => {
          const afterT = getComputedStyle(w).transform;
          const afterI = gs.activeIndex;
          this._useHardSlide = afterI === beforeI || afterT === beforeT;
          if (gs.params.loop) gs.slideToLoop(beforeI, 0, false);
          else gs.slideTo(beforeI, 0, false);
        }, 0);
      }

      installImageLoadFix() {
        if (!this.elements?.gallery) return;
        this._onImgLoad = this.debounce(this.repositionActive, 50);
        this.elements.gallery.addEventListener("load", this._onImgLoad, true);
      }

      async fixAfterVariantChange() {
        await this.waitForImages(this.elements?.gallery);

        this.resetGalleryPosition();
        this.repositionActive();
      }

      /* ========= Lifecycle ========= */
      constructor() {
        super();
      }

      connectedCallback() {
        this.isProductPage = this.dataset.productPage === "";
        this.galleryLoader = this.querySelector("#gallery-loader");

        this.readConfiguration();
        this.initializeGallery();
        this.installImageLoadFix();

        // resync initial + reset position pour éviter tout offset résiduel
        this.resyncAll();
        this.resetGalleryPosition();
        this.probeSwiperBroken();
        this.fixAfterVariantChange();

        this.galleryLoader?.classList.add("hidden");
        this.classList.remove("loading");
        this.classList.add("loaded");
        this.setStickyGallery();

        // observe les changements (variants)
        this.setupObservers();

        // Adapter sticky quand on change d’orientation / viewport
        window.addEventListener("resize", () => this.setStickyGallery(), {
          passive: true,
        });
      }

      /* ---------- Sticky: desktop uniquement ---------- */
      setStickyGallery() {
        if (!this.isProductPage) return;
        const isDesktop = window.matchMedia("(min-width: 1024px)").matches;

        this.classList.toggle("wt-product__gallery--sticky", isDesktop);

        if (isDesktop) {
          const headerHeight =
            document.querySelector(".wt-header")?.offsetHeight || 0;
          this.style.setProperty("--position-top", `${headerHeight + 16}px`);
        } else {
          this.style.removeProperty("--position-top");
        }
      }

      setupObservers() {
        const G = this.querySelector("[data-gallery] [data-swiper-container]");
        const T = this.querySelector("[data-thumbs]  [data-swiper-container]");
        const cfg = { childList: true };
        const resync = this.debounce(() => {
          this.resyncAll();
          this.fixAfterVariantChange();
        }, 40);
        try {
          this._obsG?.disconnect?.();
          this._obsT?.disconnect?.();
          this._obsG = new MutationObserver(resync);
          this._obsT = new MutationObserver(resync);
          if (G) this._obsG.observe(G, cfg);
          if (T) this._obsT.observe(T, cfg);
        } catch {}
      }

      readConfiguration() {
        this.elements = {
          section: this,
          gallery: this.querySelector("[data-gallery]"),
          thumbs: this.querySelector("[data-thumbs]"),
          galleryContainer: this.querySelector("[data-gallery]")?.querySelector(
            "[data-swiper-container]"
          ),
          thumbsContainer: this.querySelector("[data-thumbs]")?.querySelector(
            "[data-swiper-container]"
          ),
        };

        // IDs stables + ordre maître
        this.ensureStableIdsBoth(
          this.elements.galleryContainer,
          this.elements.thumbsContainer
        );
        this.masterOrder = this.buildMasterOrder(
          this.elements.galleryContainer
        );
        this.sortContainerByMaster(this.elements.galleryContainer);
        this.sortContainerByMaster(this.elements.thumbsContainer);

        // clones éventuels (si filtrage plus tard)
        this.elements.gallerySlides = Array.from(
          this.elements.gallery.querySelectorAll("[data-swiper-slide]")
        ).map((e) => e.cloneNode(true));
        this.elements.thumbsSlides = Array.from(
          this.elements.thumbs.querySelectorAll("[data-swiper-slide]")
        ).map((e) => e.cloneNode(true));

        const default_configuration = {
          sliderEnabledBreakpoint: 900,
          desktopLayout: "carousel-vertical",
        };
        const custom_configuration = this.safeParse(
          this.elements.section.querySelector("[data-configuration]")
        );
        this.configuration = {
          ...default_configuration,
          ...custom_configuration,
        };

        const autoHeightEnabled = window.innerWidth <= 768;

        const default_gallery_configuration = {
          // autoHeight: autoHeightEnabled
          autoHeight: false,
          threshold: 10,
          grabCursor: true,
          navigation: {
            nextEl: ".wt-slider__nav-next",
            prevEl: ".wt-slider__nav-prev",
          },
          scrollbar: { el: ".wt-slider__scrollbar", draggable: true },
          pagination: { el: ".swiper-pagination", type: "fraction" },
        };
        const custom_gallery_configuration = this.safeParse(
          this.elements.gallery.querySelector("[data-swiper-configuration]")
        );
        this.gallery_configuration = {
          ...default_gallery_configuration,
          ...custom_gallery_configuration,
        };

        const default_thumbs_configuration = {
          grabCursor: true,
          spaceBetween: 8,
          slidesPerView: 4,
          freeMode: false,
          threshold: 5,
          direction: "horizontal",
          watchSlidesVisibility: true,
          watchSlidesProgress: true,
          watchOverflow: true,
          navigation: {
            nextEl: ".wt-slider__nav-next",
            prevEl: ".wt-slider__nav-prev",
          },
        };
        const custom_thumbs_configuration = this.safeParse(
          this.elements.thumbs.querySelector("[data-swiper-configuration]")
        );
        this.thumbs_configuration = {
          ...default_thumbs_configuration,
          ...custom_thumbs_configuration,
        };
      }

      /* ========= Init ========= */
      initializeGallery() {
        // window.addEventListener("resize", () => this.updateSwiperAutoHeight(), {
        //   passive: true,
        // });

        this.labels = document.querySelectorAll(".f-swatch__list__link");

        this.labels.forEach((label) => {
          label.addEventListener("click", this.updateColors);
        });
        this.updateColors();

        this.initializeCarouselGallery();
      }

      updateColors = (e) => {
        let label;

        if (e) {
          label = e.target;
        } else {
          const wrapper = document.querySelector(
            ".f-swatch__list__item input:checked"
          ).parentElement;

          label = wrapper.querySelector(".f-swatch__list__link");
        }

        const color = label.querySelector(".f-swatch__list__link__content")
          .style.backgroundColor;

        const stars = document.querySelectorAll(".jdgm-star");
        stars.forEach((star) => {
          star.style.color = color;
        });

        const observer = new MutationObserver(() => {
          const comparePrice = document.querySelector(
            ".loop-widget-purchase-option-compare-at-price"
          );
          const price = document.querySelector(
            ".loop-widget-purchase-option-price"
          );

          setTimeout(() => {
            const stickyButton = document.querySelector(
              ".Vtl-StickyAddToCart__ProductPriceDiscounted"
            );

            if (stickyButton && price) {
              stickyButton.innerHTML = price.innerHTML;
            }
          }, 2000);
          const comparePriceBtn = document.querySelector(".compare-price");
          const newPriceBtn = document.querySelector(".new-price");

          const button = document.querySelector(".product-form__submit");
          button.style.backgroundColor = color;

          if (comparePriceBtn && comparePrice) {
            comparePriceBtn.innerHTML = comparePrice.innerHTML;
          }
          if (newPriceBtn && price) {
            newPriceBtn.innerHTML = price.innerHTML;
          }

          const discoveryOffer = document.getElementById(
            "loop-widget-purchase-option-id-573ea71bf0d9948ee9e63896aefd479e875e7905"
          );
          const oneTime = document.querySelector(
            ".loop-widget-purchase-option-onetime"
          );

          if (oneTime) {
            oneTime.addEventListener("click", () => {
              oneTime.style.borderWidth = "2px";
              oneTime.style.borderColor = color;

              comparePriceBtn.innerHTML = "";
              newPriceBtn.innerHTML = comparePrice.innerHTML;

              const stickyButton = document.querySelector(
                ".Vtl-StickyAddToCart__ProductPriceDiscounted"
              );

              if (stickyButton) {
                stickyButton.innerHTML = comparePrice.innerHTML;
              }

              discoveryOffer.style.borderColor = "";
              discoveryOffer.style.borderWidth = "1px";
            });
          }

          if (discoveryOffer) {
            discoveryOffer.addEventListener("click", () => {
              discoveryOffer.style.borderWidth = "2px";
              discoveryOffer.style.borderColor = color;

              comparePriceBtn.innerHTML = comparePrice.innerHTML;
              newPriceBtn.innerHTML = price.innerHTML;

              const stickyButton = document.querySelector(
                ".Vtl-StickyAddToCart__ProductPriceDiscounted"
              );

              if (stickyButton) {
                stickyButton.innerHTML = price.innerHTML;
              }

              oneTime.style.borderWidth = "1px";
              oneTime.style.borderColor = "";
            });

            // oneTime.style.borderWidth = "2px";
            // oneTime.style.borderColor = co:lor;

            discoveryOffer.style.borderWidth = "2px";
            discoveryOffer.style.borderColor = color;
            observer.disconnect(); // on arrête d'observer
          }
        });

        observer.observe(document.body, { childList: true, subtree: true });
      };

      initializeCarouselGallery() {
        if (this.thumbsSwiper == null) {
          this.decorateSwiper(
            this.elements.thumbs,
            "wt-slider__container--thumbs"
          );
          this.thumbsSwiper = this.swiperThumbsInitilize();
        }
        this.decorateSwiper(this.elements.gallery);
        this.swiperGalleryInitilize();

        const disableTouchSlide =
          this.elements.gallery.querySelector(".disable-touch");
        if (disableTouchSlide)
          disableTouchSlide.classList.add("swiper-no-swiping");

        this.syncThumbsToGalleryOrder();
        this.bindThumbDelegation(); // clic DOM (avec fallback)
      }

      swiperGalleryInitilize() {
        if (this.gallerySwiper == null) {
          const desktopRatio = this.getAttribute("desktop-ratio");
          const useAlwaysAutoHeight =
            (this.configuration.desktopLayout === "carousel-vertical" &&
              desktopRatio === "original") ||
            this.configuration.desktopLayout === "carousel-horizontal";
          const autoHeightEnabled = useAlwaysAutoHeight
            ? true
            : window.innerWidth <= 900;

          this.gallery_configuration = {
            ...this.gallery_configuration,
            autoHeight: false,
          };
          this.gallerySwiper = new Swiper(
            this.elements.gallery,
            this.gallery_configuration
          );

          // détecte si Swiper “bouge”, sinon fallback
          this._useHardSlide = false;
          this.probeSwiperBroken();
        }
      }

      swiperThumbsInitilize() {
        const autoHeightEnabled = window.innerWidth <= 900;
        this.thumbs_configuration = {
          ...this.thumbs_configuration,
          autoHeight: autoHeightEnabled,
        };
        return new Swiper(this.elements.thumbs, this.thumbs_configuration);
      }

      /* ========= Clics vignettes ========= */
      bindThumbDelegation() {
        const area = this.elements.thumbs;
        if (!area) return;

        area.querySelectorAll("[data-swiper-slide]").forEach((s) => {
          s.style.cursor = "pointer";
        });

        if (this._thumbClick)
          area.removeEventListener("click", this._thumbClick, true);
        if (this._thumbTouch)
          area.removeEventListener("touchend", this._thumbTouch, true);

        const goToIndex = (slideEl) => {
          if (!this.gallerySwiper) return;
          const realThumbs = Array.from(
            this.elements.thumbs.querySelectorAll("[data-swiper-slide]")
          ).filter((s) => !s.classList.contains("swiper-slide-duplicate"));
          const idx = realThumbs.indexOf(slideEl);
          if (idx < 0) return;

          if (this._useHardSlide) this.hardSlideTo(idx);
          else {
            this.gallerySwiper.updateSlides();
            if (this.gallerySwiper.params.loop)
              this.gallerySwiper.slideToLoop(idx, 0, false);
            else this.gallerySwiper.slideTo(idx, 0, false);
          }
        };

        this._thumbClick = (e) => {
          const slide = e.target.closest("[data-thumbs] [data-swiper-slide]");
          if (!slide) return;
          const a = e.target.closest("a");
          if (a) e.preventDefault();
          e.stopPropagation();
          goToIndex(slide);
        };
        this._thumbTouch = (e) => {
          const slide = e.target.closest("[data-thumbs] [data-swiper-slide]");
          if (!slide) return;
          goToIndex(slide);
        };

        area.addEventListener("click", this._thumbClick, true);
        area.addEventListener("touchend", this._thumbTouch, true);
      }

      /* ========= Décorateurs & resync ========= */
      decorateSwiper(el, element_class) {
        if (!el) return;
        el.classList.add("swiper", "wt-slider__container");
        if (element_class) el.classList.add(element_class);
        const wrapper = el.querySelector("[data-swiper-container]");
        if (wrapper)
          wrapper.classList.add("swiper-wrapper", "wt-slider__wrapper");
        el.querySelectorAll("[data-swiper-slide]").forEach((e) =>
          e.classList.add("swiper-slide", "wt-slider__slide")
        );
      }
      undecorateSwiper(el, element_class) {
        if (!el) return;
        el.classList.remove("swiper", "wt-slider__container");
        if (element_class) el.classList.remove(element_class);
        const wrapper = el.querySelector("[data-swiper-container]");
        if (wrapper)
          wrapper.classList.remove("swiper-wrapper", "wt-slider__wrapper");
        el.querySelectorAll("[data-swiper-slide]").forEach((e) =>
          e.classList.remove("swiper-slide", "wt-slider__slide")
        );
      }

      resyncAll = () => {
        const G = this.elements?.galleryContainer;
        const T = this.elements?.thumbsContainer;
        if (!G || !T) return;

        this.ensureStableIdsBoth(G, T);
        this.masterOrder = this.buildMasterOrder(G);
        this.sortContainerByMaster(G);
        this.sortContainerByMaster(T);

        if (this.gallerySwiper) {
          const gSlides = Array.from(
            this.elements.gallery.querySelectorAll("[data-swiper-slide]")
          );
          this.gallerySwiper.removeAllSlides();
          this.gallerySwiper.appendSlide(gSlides);
          this.gallerySwiper.updateSlides();
          this.gallerySwiper.update();
        }
        if (this.thumbsSwiper) this.syncThumbsToGalleryOrder();

        this.resetGalleryPosition();
        this.probeSwiperBroken();
      };

      // updateSwiperAutoHeight = () => {
      //   const bp = this.configuration?.sliderEnabledBreakpoint || 900;
      //   const now = window.innerWidth < bp ? "mobile" : "desktop";
      //   if (this._resState !== now) {
      //     this._resState = now;
      //     this.resyncAll();
      //     this.fixAfterVariantChange();
      //   }
      // };
    }
  );
}
